import Keycloak from 'keycloak-js';
import iconClose from '../../../../assets/icon-close.svg';
import { Dispatch, ReactElement, SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectHasOpenBestellung } from '../../../../store/Workflow.store';
import { clearBestellung } from '../../../../store/Bestellung.store';
import { ALLOW_COOKIES_KEY } from '../../../../localStorage';
import { showConfirmationDialog } from '../../ConfirmationDialog/confirmationDialog';
import styles from '../Shell.module.scss';
import accountIcon from '../../../../assets/icon-account.svg';
import FlutterBridge from '../../../../flutter/flutterBridge';
import { usePostHog } from 'posthog-js/react';
import type { RootState } from '../../../../configureStore';
import { NachrichtenBell } from './NachrichtenBell';

type AccountProps = {
    username: string;
    keycloak: Partial<Keycloak.KeycloakInstance>;
    setMenuIsOpen: Dispatch<SetStateAction<boolean>>;
    hasUnreadMessages: boolean;
    onNachrichtenBellMarkReadOptimistic: () => void;
    showNachrichtenBell?: boolean;
};

export const Account = ({
    username,
    keycloak,
    setMenuIsOpen,
    hasUnreadMessages,
    onNachrichtenBellMarkReadOptimistic,
    showNachrichtenBell = true,
}: AccountProps): ReactElement => {
    const dispatch = useDispatch();
    const hasOpenBestellung = useSelector(selectHasOpenBestellung);
    const isAdmin = useSelector((state: RootState) => state.navigation.isAdmin);
    const posthog = usePostHog();

    const closeMenu = (): void => {
        setMenuIsOpen(false);
    };

    const logout = (logoutUrl: string): void => {
        dispatch(clearBestellung());
        FlutterBridge.sendToken(null);
        const allowCookies = localStorage.getItem(ALLOW_COOKIES_KEY);
        localStorage.clear();
        if (allowCookies !== null) {
            localStorage.setItem(ALLOW_COOKIES_KEY, allowCookies);
        }

        window.location.href = logoutUrl;
    };

    const confirmLogoutDialog = (logoutUrl: string): void => {
        showConfirmationDialog({
            message: (
                <>
                    Sie haben noch eine Bestellung, die nicht abgeschlossen ist. Wenn Sie fortfahren, werden Ihre Bestellpositionen
                    gelöscht.
                </>
            ),
            okButtonText: 'Bestellpositionen löschen',
            onOkCallback: () => {
                posthog.reset();
                logout(logoutUrl);
            },
        });
    };

    const shouldLogout = () => {
        setMenuIsOpen(false);
        const redirectUri = `${window.location.origin}/`;
        const logoutUrl = (keycloak.createLogoutUrl && keycloak?.createLogoutUrl?.({ redirectUri })) || '';
        if (hasOpenBestellung) {
            confirmLogoutDialog(logoutUrl);
        } else {
            posthog.reset();
            logout(logoutUrl);
        }
    };

    return (
        <div>
            <div className={styles._accountContainer}>
                <div className={styles._userLogoutContainer}>
                    <Link onClick={closeMenu} to={{ pathname: '/accountSettings' }} className={styles._accountIconLink}>
                        <img src={accountIcon} className={styles._accountIcon} alt="Benutzer" />
                    </Link>

                    <div>
                        <label className={styles._menuLabel}>{username}</label>
                        <button onClick={shouldLogout} className={styles._account} id="login-logout-button">
                            Abmelden
                        </button>
                    </div>
                </div>

                <div className={styles._notificationAndCloseContainer}>
                    {showNachrichtenBell && !isAdmin && (
                        <NachrichtenBell
                            hasUnreadMessages={hasUnreadMessages}
                            onMarkReadOptimistic={onNachrichtenBellMarkReadOptimistic}
                            onAfterClick={() => setMenuIsOpen(false)}
                        />
                    )}
                    <img src={iconClose} alt="Navigation schließen" className={styles._menuCloseIcon} onClick={closeMenu} />
                </div>
            </div>
        </div>
    );
};
